import { IDB_flowReports, IDB_posts, IDB_update } from '../IDB/IDB.dexie'
import { Post_createPostId, Post_reportPostToSyncPost } from '../Post/Post.TPost'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import { getVersionIdentifier } from '../../../shared/domain/workflows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Post_readReportPost, Post_reportPostCreate } from '../Post/Post.reportPost'
import type { Type_Workflow } from '../Type/Type.hive'
import { IDB_workflowRead } from '../IDB/IDB.table.workflow'
import type { OngoingReportIn } from '../../../shared/domain/reports/ongoingReport.no-deps'
import { Cust_hasFeature } from '../Cust/Cust.hasFeature'
import { isOnline } from '../Serv/Serv.socketClient'

export async function Flow_uploadNewReports(logger: ClientLogger) {
    // Get the ids of all the local reports
    const localReportIds = await IDB_flowReports().orderBy('startDate').primaryKeys()

    // Find new reports to upload
    for (const reportId of localReportIds) {
        await uploadNewReport(logger, reportId).catch(logger.error)
    }
}

export async function tryUploadNewReport(logger: ClientLogger, reportClientId: ReportClientId) {
    const online = await isOnline()
    if (!online) {
        return
    }

    try {
        await uploadNewReport(logger, reportClientId)
    } catch ({ message }) {
        logger.warn('Failed to upload new report on open', {
            message,
            reportClientId,
        })
    }
}

export async function uploadNewReport(logger: ClientLogger, reportClientId: ReportClientId) {
    const flowReport = await IDB_flowReports().get(reportClientId)

    // Ignore old reports before migration
    if (flowReport.collaborators == null) return

    // If not pending upload, bail
    if (flowReport.uploadState !== 'pendingUpload' && flowReport.uploadState !== 'pendingUploadAndFinalisation') return

    let post = await Post_readReportPost(logger, flowReport.id)

    if (post == null) {
        const postId = Post_createPostId()
        const workflow: Type_Workflow = await IDB_workflowRead(flowReport.workflow.id)
        const postWorkflow: Pick<Type_Workflow, 'name' | 'id'> = {
            id: flowReport.workflow.id,
            name: workflow.name,
        }

        post = await Post_reportPostCreate(logger, {
            postId,
            reportClientId: flowReport.id,
            title: null,
            locationId: flowReport.location,
            procedure: flowReport.procedure,
            workflow: postWorkflow,
            startDate: flowReport.startDate,
            stepsCompleted: 0,
            reportStatusId: flowReport.reportStatusId,
            subjectUnitId: flowReport.subjectUnitId,
            reportParentClientId: flowReport.parentClientId,
            inProgress: true,
        })
    }

    const ongoingReportIn: OngoingReportIn = {
        clientId: flowReport.id,
        procedure: flowReport.procedure.id,
        workflow: getVersionIdentifier(flowReport.workflow),
        scheduledInstance: flowReport.scheduledInstance?.id ?? null,
        startDate: flowReport.startDate,
        teamDoneFor: flowReport.teamDoneFor,
        origin: flowReport.origin,
        isAction: flowReport.isAction,
        flowUnitIds: flowReport.flowUnitIds,
        loopPlan: flowReport.loopPlan,
        cachedUnitData: flowReport.cachedUnitData,
        reportStatusId: flowReport.reportStatusId,
        subjectUnitId: flowReport.subjectUnitId,
        subjectUnitData: flowReport.subjectUnitData,
        subjectUnitVersion: flowReport.subjectUnitVersion,
        parentClientId: flowReport.parentClientId,
        progenitorClientId: flowReport.progenitorClientId,
        parentSubjectUnitId: flowReport.parentSubjectUnitId,
        coordinates: { lat: flowReport.coordinates?.lat, lon: flowReport.coordinates?.lon },
        post: Post_reportPostToSyncPost(post),
        calendarEvent: {
            data: flowReport.calendarEvent.data,
            startDate: flowReport.calendarEvent.startDate,
            endDate: flowReport.calendarEvent.endDate,
            recurring: flowReport.calendarEvent.recurring,
            location: flowReport.calendarEvent.location,
            exceptions: flowReport.calendarEvent.exceptions,
            series: flowReport.calendarEvent.series,
            recurrencePattern: flowReport.calendarEvent.recurrencePattern,
            template: flowReport.calendarEvent.template,
            campaign: flowReport.calendarEvent.campaign,
        },
        location: flowReport.location,
    }

    if (!Cust_hasFeature('report.loopPlan')) {
        delete ongoingReportIn.loopPlan
        delete ongoingReportIn.cachedUnitData
    }

    const { ongoingReportId } = await Serv_clientWithLogger(logger).startReport(ongoingReportIn)
    await IDB_update(IDB_posts(), post.id, { report: ongoingReportId, status: 'synced' })
    await IDB_update(IDB_flowReports(), reportClientId, { uploadState: 'uploaded', serverId: ongoingReportId })
}
